import { useCallback, useState } from 'react'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { getReposWithBranches, updateOrgRepos } from '~/services/organizationService'
import { RepositoryBranches } from '~/types/ResponseType'

export function useRepositoryBranches() {
  const { currentWorkspace } = useWorkspaceContext()
  const [repositoryBranches, setRepositoryBranches] = useState<RepositoryBranches[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const filterBranches = useCallback((branches: string[]) => {
    return branches.filter(
      (branch: string) => !branch.startsWith('curie-dev') && !branch.startsWith('curie_dev')
    )
  }, [])

  const loadRepositoriesAndBranches = useCallback(
    async (connType: string) => {
      try {
        setIsLoading(true)

        // Validate connType before making API calls
        if (!connType || typeof connType !== 'string') {
          console.error('Invalid connType provided:', connType)
          setRepositoryBranches([])
          return []
        }

        // Validate workspace context
        if (!currentWorkspace?.uuid) {
          console.error('Workspace UUID not available')
          setRepositoryBranches([])
          return []
        }

        // Load repositories with branches in a single optimized API call
        const reposWithBranches = await getReposWithBranches(connType, currentWorkspace.uuid)

        // Transform to internal Repository format and filter out curie-dev branches.
        // Auto-select the default branch for repos that have no previously selected branches.
        const repositories = reposWithBranches.map(({ name, branches, default_branch }) => {
          const filtered = filterBranches(branches)
          // If no branches were previously selected and a default branch is known, pre-select it
          if (filtered.length === 0 && default_branch) {
            return { name, branches: [default_branch], default_branch }
          }
          return { name, branches: filtered, default_branch }
        })

        setRepositoryBranches(repositories)
        return repositories
      } catch (error) {
        console.error('useRepositoryBranches: Error loading repositories:', error)
        setRepositoryBranches([]) // Set empty array on error
        return []
      } finally {
        setIsLoading(false)
      }
    },
    [filterBranches, currentWorkspace]
  )

  const submitRepositories = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (connType: string, traceFunc?: (point: any) => Promise<any> | void) => {
      try {
        setIsSubmitting(true)

        // Validate connType before making API calls
        if (!connType || typeof connType !== 'string') {
          console.error('Invalid connType provided for submission:', connType)
          return false
        }

        // Validate workspace context
        if (!currentWorkspace?.uuid) {
          console.error('Workspace UUID not available for submission')
          return false
        }

        const reposToUse = repositoryBranches.filter(repo => repo.branches.length > 0)

        await updateOrgRepos(connType, reposToUse, currentWorkspace.uuid)

        if (traceFunc) {
          await traceFunc({
            event: 'repo_setup',
            labels: {
              conn_type: connType,
              repositoryBranches: reposToUse.map(repo => repo.name).join(',')
            }
          })
        }

        return true
      } catch (error) {
        console.error('Error submitting repositories:', error)
        return false
      } finally {
        setIsSubmitting(false)
      }
    },
    [repositoryBranches, currentWorkspace]
  )

  return {
    repositoryBranches,
    setRepositoryBranches,
    isLoading,
    isSubmitting,
    loadRepositoriesAndBranches,
    submitRepositories
  }
}
